import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Package, Clock, CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { quoteService } from '../services/quoteService';
import type { Quote } from '../types/quotes';

const CustomerOrderDetail: React.FC = () => {
  const { user } = useAuth();
  const [order, setOrder] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);

  const loadOrder = useCallback(async (orderId: string) => {
    if (!user?.uid) return;
    
    try {
      const orderData = await quoteService.getQuote(orderId);
      
      // Security: Verify this is the user's order
      if (orderData && orderData.createdByUserId === user.uid) {
        setOrder(orderData);
      } else {
        console.error('Unauthorized access to order');
      }
    } catch (error) {
      console.error('Error loading order:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    const orderId = window.location.pathname.split('/').pop();
    if (orderId) {
      loadOrder(orderId);
    }
  }, [loadOrder]);

  const getStatusInfo = (status: Quote['status']) => {
    const info = {
      draft: {
        label: 'Entwurf',
        color: 'gray',
        description: 'Dies ist ein Entwurf.',
        icon: Package
      },
      submitted: {
        label: 'Eingereicht',
        color: 'blue',
        description: 'Deine Bestellung ist bei uns eingegangen. Wir prüfen die Details und melden uns in Kürze!',
        icon: Package
      },
      in_progress: {
        label: 'In Bearbeitung',
        color: 'yellow',
        description: 'Wir arbeiten bereits an deinem Projekt! 🚀',
        icon: Clock
      },
      completed: {
        label: 'Abgeschlossen',
        color: 'green',
        description: 'Dein Projekt ist fertig! Schau dir das Ergebnis an. 🎉',
        icon: CheckCircle
      },
      cancelled: {
        label: 'Storniert',
        color: 'gray',
        description: 'Diese Bestellung wurde storniert.',
        icon: AlertCircle
      }
    };
    return info[status];
  };

  const getFragebogenStatus = (order: Quote) => {
    if (order.fragebogenSubmitted) {
      return (
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">Eingereicht</span>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-2 text-orange-600">
        <AlertCircle className="w-5 h-5" />
        <span className="font-medium">Ausstehend</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <p className="text-gray-600 mb-4">Bestellung nicht gefunden.</p>
        <button
          onClick={() => window.location.href = '/my-orders'}
          className="text-blue-600 hover:text-blue-800"
        >
          ← Zurück zu meinen Bestellungen
        </button>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Back Button */}
      <button
        onClick={() => window.location.href = '/my-orders'}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück zu meinen Bestellungen
      </button>

      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Bestellung #{order.id.substring(0, 8)}
            </h1>
            <p className="text-gray-600">
              Bestellt am {order.submittedAt.toLocaleDateString('de-DE', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-gray-900">
              €{order.amountTotal.toFixed(2)}
            </p>
            <p className="text-sm text-gray-500">{order.totalHours} Stunden</p>
          </div>
        </div>

        {/* Status */}
        <div className={`p-4 rounded-lg bg-${statusInfo.color}-50 border border-${statusInfo.color}-200`}>
          <div className="flex items-start gap-3">
            <StatusIcon className={`w-6 h-6 text-${statusInfo.color}-600 mt-0.5 flex-shrink-0`} />
            <div>
              <p className={`font-semibold text-${statusInfo.color}-900 mb-1`}>
                Status: {statusInfo.label}
              </p>
              <p className={`text-sm text-${statusInfo.color}-800`}>
                {statusInfo.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modules */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Gewählte Module
        </h2>
        <div className="space-y-2">
          {(() => {
            // Try to get detailed module info from configuration
            const configModules = (order.configuration?.selectedModules as Array<{ name: string; hours: number }>) || [];
            const moduleMap = new Map(configModules.map(m => [m.name, m.hours]));
            
            return order.selectedModules.map((moduleName, idx) => {
              const moduleHours = moduleMap.get(moduleName);
              return (
                <div
                  key={idx}
                  className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center">
                    <span className="text-gray-700">{moduleName}</span>
                    {moduleHours !== undefined && (
                      <span className="ml-3 text-sm text-gray-500">({moduleHours}h)</span>
                    )}
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Price Breakdown */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Preisübersicht
        </h2>
        <div className="space-y-3">
          <div className="flex justify-between text-gray-600">
            <span>Stunden gesamt:</span>
            <span className="font-medium">{order.totalHours}h</span>
          </div>
          
          {order.discountPercent && order.discountPercent > 0 && (
            <>
              <div className="flex justify-between text-gray-600">
                <span>Zwischensumme:</span>
                <span className="font-medium">
                  €{((order.amountTotal / (1 - order.discountPercent / 100))).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>Partnerrabatt ({order.discountPercent}%):</span>
                <span className="font-medium">-€{order.discountAmount?.toFixed(2)}</span>
              </div>
            </>
          )}
          
          <div className="flex justify-between text-xl font-bold text-gray-900 pt-3 border-t border-gray-200">
            <span>Gesamtpreis:</span>
            <span>€{order.amountTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Kick-Off Fragebogen */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Kick-Off Fragebogen
            </h2>
            {getFragebogenStatus(order)}
            {!order.fragebogenSubmitted && (
              <p className="text-sm text-gray-600 mt-2">
                Füll den Fragebogen aus, damit wir direkt loslegen können! 🚀
              </p>
            )}
          </div>
          {!order.fragebogenSubmitted && (
            <button
              onClick={() => window.location.href = `/kickoff?quoteId=${order.id}`}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              Jetzt ausfüllen
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          {order.fragebogenSubmitted && order.fragebogenSubmittedAt && (
            <div className="text-right text-sm text-gray-500">
              Eingereicht am{' '}
              {order.fragebogenSubmittedAt.toLocaleDateString('de-DE')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerOrderDetail;

